import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { useGuestAuth } from '../contexts/GuestAuthContext';

const Header = ({ transparent = false }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, loading, loginWithGoogle, logout } = useGuestAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Properties', href: '/properties' },
    { name: 'Book Now', href: '/properties' },
    { name: 'Local Guide', href: '/local-guide' },
    { name: 'Blog', href: '/blog' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';
  const shouldBeTransparent = isHomePage && !scrolled;

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      shouldBeTransparent 
        ? 'bg-transparent' 
        : 'bg-slate-900 shadow-lg'
    }`}>
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Top">
        <div className="flex w-full items-center justify-between py-5">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <div className="text-left">
                <div className={`text-2xl font-serif font-bold tracking-tight transition-all duration-300 ${
                  shouldBeTransparent ? 'text-white' : 'text-white'
                } group-hover:text-amber-400 drop-shadow-lg`}>
                  CAPITOL VIEW
                </div>
                <div className={`text-xs font-light tracking-[0.3em] uppercase transition-all duration-300 -mt-1 ${
                  shouldBeTransparent ? 'text-amber-400' : 'text-amber-400'
                }`}>
                  Stays
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-8">
            {navigation.map((item) => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm font-medium transition-colors duration-200 ${
                    shouldBeTransparent
                      ? 'text-white hover:text-amber-400'
                      : 'text-slate-200 hover:text-amber-400'
                  }`}
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-amber-400'
                      : shouldBeTransparent
                      ? 'text-white hover:text-amber-400'
                      : 'text-slate-200 hover:text-amber-400'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button & User Auth */}
          <div className="hidden lg:flex lg:items-center lg:gap-3">
            {/* Google Sign In / User Profile */}
            {!loading && (
              isAuthenticated && user ? (
                <div className="flex items-center gap-2">
                  {/* Favorites Link */}
                  <Link to="/favorites" className="p-2 rounded-full hover:bg-slate-800 transition-colors">
                    <Heart className="h-5 w-5 text-slate-300 hover:text-red-400" />
                  </Link>
                  {user.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-8 h-8 rounded-full border-2 border-amber-400"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                      <User className="h-4 w-4 text-slate-900" />
                    </div>
                  )}
                  <span className="text-sm text-white font-medium">
                    {user.name?.split(' ')[0]}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={logout}
                    className="text-slate-300 hover:text-white hover:bg-slate-800"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loginWithGoogle}
                  className="text-slate-200 hover:text-amber-400 hover:bg-slate-800"
                  data-testid="header-google-signin"
                >
                  <User className="h-4 w-4 mr-2" />
                  Sign In
                </Button>
              )
            )}
            <Link to="/properties">
              <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-6 shadow-lg hover:shadow-xl transition-all duration-200">
                Book Direct & Save
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <Button
              variant="ghost"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:bg-slate-800"
            >
              <span className="sr-only">Open menu</span>
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 bg-slate-900 rounded-b-lg mt-2">
            <div className="space-y-1 pt-2">
              {navigation.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 text-slate-200 hover:bg-slate-800 hover:text-amber-400"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                      isActive(item.href)
                        ? 'bg-slate-800 text-amber-400'
                        : 'text-slate-200 hover:bg-slate-800 hover:text-amber-400'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              <Link to="/properties" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                  Book Direct & Save
                </Button>
              </Link>
              {/* Mobile Sign In */}
              {!loading && (
                isAuthenticated && user ? (
                  <div className="flex items-center justify-between px-3 py-3 mt-2 bg-slate-800 rounded-md">
                    <div className="flex items-center gap-2">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                          <User className="h-4 w-4 text-slate-900" />
                        </div>
                      )}
                      <span className="text-sm text-white">{user.name}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-slate-300">
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full mt-2 border-slate-600 text-white hover:bg-slate-800"
                    onClick={() => { loginWithGoogle(); setMobileMenuOpen(false); }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    Sign In with Google
                  </Button>
                )
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;